import { z } from "zod"

/** Four hours. Caps a measurement so a broken client can't skew averages. */
export const MAX_ACTIVE_SECONDS = 4 * 60 * 60

/** Active seconds a mechanic spent on a screen, measured by the CRM. */
export const ActiveSecondsSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_ACTIVE_SECONDS)

/** Above this a counter is a broken client, not a mechanic. */
const MAX_EFFORT_COUNT = 100_000
/** Hundreds of kilometres of mouse travel or scrolling. */
const MAX_EFFORT_PX = 1_000_000_000

const EffortCountSchema = z.number().int().min(0).max(MAX_EFFORT_COUNT)
const EffortPxSchema = z.number().int().min(0).max(MAX_EFFORT_PX)

/**
 * Mobile is a narrow viewport, tablet a wide one driven by touch, desktop a
 * wide one with a mouse.
 */
export const DeviceClassSchema = z.enum(["MOBILE", "TABLET", "DESKTOP"])
export type DeviceClass = z.infer<typeof DeviceClassSchema>

/**
 * Physical effort a mechanic spent on a screen, measured by the CRM alongside
 * the active seconds. See the TaskEffort model in the API.
 */
export const InteractionEffortSchema = z.object({
  deviceClass: DeviceClassSchema,
  viewportWidth: z.number().int().min(0).max(100_000),
  viewportHeight: z.number().int().min(0).max(100_000),
  clicks: EffortCountSchema,
  taps: EffortCountSchema,
  keystrokes: EffortCountSchema,
  pointerDistancePx: EffortPxSchema,
  scrollPx: EffortPxSchema,
  sentryReplayId: z.string().max(64).optional(),
})
export type InteractionEffort = z.infer<typeof InteractionEffortSchema>

/** How long the job's screens took. See the JobTiming model in the API. */
export const JobTimingSchema = z.object({
  receptionSeconds: z.number().nullable(),
  inspectionSeconds: z.number().nullable(),
  addonOrderSeconds: z.number(),
  addonOrderCount: z.number(),
  inspectionResponseSeconds: z.number().nullable(),
  addonOrderResponseSeconds: z.number(),
  addonOrderResponseCount: z.number(),
})

export type JobTiming = z.infer<typeof JobTimingSchema>

/** "45 s", "6 min 3 s", "1 h 20 min", "2 d 3 h". */
export const formatDurationSeconds = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.round(totalSeconds))
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = seconds % 60

  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`
  if (minutes > 0)
    return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`
  return `${rest} s`
}

/**
 * Work time: "45 min", "1 h 20 min", "26 h". Never days, since a day's summed
 * workshop time reads as hours of work, not calendar days.
 */
export const formatDurationMinutes = (totalMinutes: number): string => {
  const minutes = Math.max(0, Math.round(totalMinutes))
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60

  if (hours === 0) return `${rest} min`
  return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`
}

/**
 * A pause between two interactions longer than this means the mechanic left
 * the screen (a customer walked in, say). Such a gap counts as zero.
 */
export const IDLE_GAP_THRESHOLD_MS = 90_000

/** The part of a gap between two interactions that counts as active time. */
export const activeMsForGap = (gapMs: number): number =>
  gapMs > 0 && gapMs <= IDLE_GAP_THRESHOLD_MS ? gapMs : 0
